import {
  IonCard,
  IonCardContent,
  IonCol,
  IonRow,
} from '@ionic/react';
import { Breadcrumb, Breadcrumbs } from 'src/components/shared/Breadcrumbs';
import { CardStat } from 'src/components/shared/CardStat';
import { LogoSupplier } from 'src/components/shared/LogoSupplier';
import { cashOutline, clipboardOutline } from 'ionicons/icons';
import { Page } from 'src/layouts/Page';
import { useEffect, useState } from 'react';

import { TableTopCustomers } from './TableTopCustomers';
import { TableTopItems } from './TableTopItems';
import { Divider } from 'src/components/shared/Divider';
import { useUtils } from 'src/hooks/useUtils';
import { Supplier } from 'src/interfaces/Supplier';
import { useSelector } from 'react-redux';
import { SelectTimeFrame, TimeFrame } from 'src/components/shared/SelectTimeFrame';
import { useApi } from 'src/hooks/useApi';
import { AppUserSelf } from 'src/interfaces/AppUserSelf';

interface SalesData {
  sales: string;
  count: number;
}

export const HomePage: React.FC = () => {
  const api = useApi();
  const utils = useUtils();
  const supplier: Supplier | null = useSelector(
    (state: any) => state.supplier.supplier
  );
  const authUser: AppUserSelf | null = useSelector(
    (state: any) => state.auth.user
  );

  const [loading, setLoading] = useState(false);
  const [salesCount, setSalesCount] = useState<SalesData | null>(null);
  const [timeFrameLabel, setTimeFrameLabel] = useState<string>('All Time');
  const [filterMinDate, setFilterMinDate] = useState<string | null>(null);

  const breadcrumbs: Array<Breadcrumb> = [{ label: 'Home' }];

  const getOrderCount = async () => {
    if (!authUser || !filterMinDate) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(
        `supplier/${authUser.organization_id}/sales`,
        {
          min_date: filterMinDate || undefined,
        }
      );

      if (response.status === 200) {
        setSalesCount(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrderCount();
  }, [filterMinDate]);

  const handleTimeFrameUpdate = (timeFrame: TimeFrame) => {
    setFilterMinDate(timeFrame.value);
    setTimeFrameLabel(timeFrame.label);
  };

  return (
    <Page title="Supplier Home" className="supplierHome">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <IonRow>
        <IonCol size="12" sizeLg="8" className="ion-no-padding ion-padding-end">
          <IonRow>
            <IonCol size="6" sizeSm="6">
              <IonCard className="d-flex ion-align-items-center ion-justify-content-center fullHeight">
                <IonCardContent className="ion-text-center">
                  <LogoSupplier src={supplier?.logo} />
                </IonCardContent>
              </IonCard>
            </IonCol>

            <IonCol size="12" sizeSm="6" className="ion-no-padding">
              <IonRow>
                <IonCol size="12">
                  <SelectTimeFrame
                    onUpdate={handleTimeFrameUpdate}
                    defaultDate={utils.subMonths(12)}
                  />
                </IonCol>
                <IonCol size="12" sizeLg="6">
                  <CardStat
                    color="primary"
                    title="Invoices"
                    stat={utils.localizeNumber(salesCount?.count ?? 0)}
                    statTitle={timeFrameLabel}
                    icon={clipboardOutline}
                    loading={loading}
                  />
                </IonCol>
                <IonCol size="12" sizeLg="6">
                  <CardStat
                    color="success"
                    title="Sales"
                    stat={utils.toUsd(Number(salesCount?.sales ?? 0), 0)}
                    statTitle={timeFrameLabel}
                    icon={cashOutline}
                    loading={loading}
                  />
                </IonCol>
              </IonRow>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol className="ion-no-padding ion-padding-horizontal">
              <Divider />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonCard>
                <TableTopItems minDate={filterMinDate} />
              </IonCard>
            </IonCol>
          </IonRow>
        </IonCol>
        <IonCol size="12" sizeLg="4">
          <IonCard>
            <TableTopCustomers minDate={filterMinDate} />
          </IonCard>
        </IonCol>
      </IonRow>
    </Page>
  );
};
